import { AfterViewInit, Component, effect, ElementRef, input, output, viewChild } from '@angular/core';
import { Diagram, GraphLinksModel, GraphObject, LayeredDigraphLayout, Model, Node, Part, Size } from 'gojs';
import {
  DEFAULT_DIAGRAM_OPTIONS,
  DEFAULT_MAX_IMAGE_SIZE,
  DIAGRAM_DIRECTION_ANGLES,
  DiagramDirection,
  DiagramGraphConfig,
  DiagramHostApi,
  DiagramNodeData,
  DiagramOptions,
  getImageMaxSizeOptions,
  getMaxImageSizes,
  hasImageSrcData,
  makeDiagram,
  updateSegmentIndexes,
} from './diagram-graph.utils';

@Component({
  selector: 'nk-diagram-graph',
  templateUrl: './diagram-graph.component.html',
  styleUrls: ['./diagram-graph.component.scss'],
})
export class DiagramGraphComponent implements AfterViewInit {
  public readonly height = input<string>();
  public readonly model = input.required<GraphLinksModel>();
  public readonly focusOn = input<DiagramNodeData>();
  public readonly chartOptions = input<DiagramOptions>({ ...DEFAULT_DIAGRAM_OPTIONS });
  /** Feature-specific node + link templates the diagram renders. */
  public readonly config = input.required<DiagramGraphConfig>();

  public readonly nodeSelected = output<Node | null>();
  public readonly expandUp = output<DiagramNodeData>();
  public readonly expandDown = output<DiagramNodeData>();
  public readonly deleteNode = output<DiagramNodeData>();

  public diagram!: Diagram;

  private readonly _diagramDiv = viewChild.required<ElementRef<HTMLDivElement>>('diagramDiv');

  /** Generic operations handed to the injected node template. */
  private readonly _hostApi: DiagramHostApi = {
    expandUp: (data) => this.expandUp.emit(data),
    expandDown: (data) => this.expandDown.emit(data),
    toggleCollapseUp: (node) => this._toggleCollapseFrom(node, !(node.data as DiagramNodeData).isCollapsedFrom),
    toggleCollapseDown: (node) => this._toggleCollapseTo(node, !(node.data as DiagramNodeData).isCollapsedTo),
    setHovered: (obj, hovered) => this._hoverHighlight(obj, hovered),
  };

  constructor() {
    // Scroll to the focused node whenever it changes (once the diagram exists).
    effect(() => this.scrollToNode(this.focusOn()));
  }

  private _hoverHighlight(obj: GraphObject, isHovered: boolean): void {
    const node = obj.part;
    const nodeData = (node?.data as DiagramNodeData) ?? null;
    if (nodeData?.key && node?.diagram) {
      const nodeKey = nodeData.key;
      const model = this.model();
      this.diagram.model.commit((m: Model) => {
        const nodeToUpdate = model.findNodeDataForKey(nodeData.key);
        if (nodeToUpdate) {
          m.setDataProperty(nodeToUpdate, 'isHovered', isHovered);
        }

        model.linkDataArray.forEach((link, index) => {
          const isAdjacent = link['from'] === nodeKey || link['to'] === nodeKey;
          m.setDataProperty(model.linkDataArray[index], 'isHovered', isAdjacent ? isHovered : false);
        });
      }, 'highlight hover');
    }
  }

  public setDirection(value: DiagramDirection): void {
    this.diagram.startTransaction('change Layout');
    (this.diagram.layout as LayeredDigraphLayout).direction = DIAGRAM_DIRECTION_ANGLES[value];
    this.diagram.commitTransaction('change Layout');
  }

  public setLayerSpacing(value: number): void {
    this.diagram.startTransaction('change Layout');
    (this.diagram.layout as LayeredDigraphLayout).layerSpacing = value;
    this.diagram.commitTransaction('change Layout');
  }

  public setColumnSpacing(value: number): void {
    this.diagram.startTransaction('change Layout');
    (this.diagram.layout as LayeredDigraphLayout).columnSpacing = value;
    this.diagram.commitTransaction('change Layout');
  }

  public setupDiagram(): void {
    const $make = GraphObject.make;
    this.diagram = makeDiagram(this._diagramDiv().nativeElement, $make, this.chartOptions());
    this.diagram.model = this.model();
    this.diagram.allowUndo = true;
    this.diagram.allowClipboard = true;
    this.diagram.undoManager.isEnabled = true;
    this.diagram.addDiagramListener('ObjectDoubleClicked', (e) => {
      const node = e.diagram.selection.first();
      this.nodeSelected.emit(node instanceof Node ? node : null);
    });

    this.diagram.nodeTemplate = this.config().makeNodeTemplate($make, this._hostApi);
    this.diagram.linkTemplate = this.config().makeLinkTemplate($make);
    this.diagram.addDiagramListener('InitialLayoutCompleted', (event) => {
      updateSegmentIndexes(event.diagram);

      const nodes = event.diagram.model.nodeDataArray as DiagramNodeData[];
      const parent = nodes.filter((x) => x.isTopNode)[0]; // Scroll to top node.
      if (!parent) {
        return;
      }
      const node = event.diagram.findNodeForData(parent);
      if (!node) {
        return;
      }
      event.diagram.select(node);
      let scrollRect = node.actualBounds.copy();
      scrollRect = scrollRect.setTo(scrollRect.x, 0, scrollRect.width, 0);
      event.diagram.centerRect(scrollRect);
    });

    this.diagram.addDiagramListener('SelectionDeleted', (event) => {
      if (Array.isArray(event.subject)) {
        event.subject.forEach((p) => this.deleteNode.emit((p as Node).data as DiagramNodeData)); // iterate over each node and return the nodeData
      }
    });
  }

  public nodesTo(stack: Part[], node: Part, start: Part): void {
    const it = (node as Node).findNodesInto();
    it.each((a) => {
      const child = a;
      if (stack.find((deleted) => deleted.key === child.key)) {
        return;
      }
      if (child !== start) {
        stack.push(child);
        this.nodesTo(stack, child, start);
      }
    });
  }

  public nodesFrom(stack: Part[], node: Part, start: Part): void {
    const it = (node as Node).findNodesOutOf();
    it.each((a) => {
      const parent = a;
      if (stack.find((deleted) => deleted.key === parent.key)) {
        return;
      }
      if (parent !== start) {
        stack.push(parent);
        this.nodesFrom(stack, parent, start);
      }
    });
  }

  public findNodesToCollapseUp(node: Part, setCollapsed: boolean): Part[] {
    // Get all of the children from main node.
    // Delete all parents
    // delete all parent's parents
    // delete all deleted node's children (parents and grandparent's children) except main node's children
    // keep downwards, delete upwards.
    const keepStack: Node[] = [];
    this.nodesTo(keepStack, node, node);
    let deleteStack: Node[] = [];
    this.nodesFrom(deleteStack, node, node);

    deleteStack = deleteStack.filter((toDelete) => !keepStack.some((keep) => keep.key === toDelete.key));

    // start by finding all parents to delete, then find all parents children.
    deleteStack.forEach((parentsToDelete) => {
      this.nodesTo(deleteStack, parentsToDelete, node);
      this.nodesFrom(deleteStack, parentsToDelete, node);
    });

    deleteStack = deleteStack.filter((toDelete) => !keepStack.some((keep) => keep.key === toDelete.key));

    deleteStack.forEach((node) => {
      this.diagram.model.setDataProperty(node.data, 'visible', !setCollapsed);
    });

    return deleteStack;
  }

  private _toggleCollapseFrom(node: Part, setCollapsed: boolean): void {
    this.diagram.startTransaction('collapse nodes from');
    this.diagram.model.setDataProperty(node.data, 'isCollapsedFrom', setCollapsed);
    this.findNodesToCollapseUp(node, setCollapsed);
    this.diagram.commitTransaction('collapse nodes from');
  }

  public findNodesToCollapseDown(node: Part, setCollapsed: boolean): Node[] {
    // Get all of the children from main node.
    // Delete all parents
    // delete all parent's parents
    // delete all deleted node's children (parents and grandparent's children) except main node's children
    // keep downwards, delete upwards.
    const keepStack: Node[] = [];
    this.nodesFrom(keepStack, node, node);
    let deleteStack: Node[] = [];
    this.nodesTo(deleteStack, node, node);

    deleteStack = deleteStack.filter((toDelete) => !keepStack.some((keep) => keep.key === toDelete.key));

    // start by finding all parents to delete, then find all parents children.
    deleteStack.forEach((parentsToDelete) => {
      this.nodesFrom(deleteStack, parentsToDelete, node);
      this.nodesTo(deleteStack, parentsToDelete, node);
    });

    deleteStack = deleteStack.filter((toDelete) => !keepStack.some((keep) => keep.key === toDelete.key));

    deleteStack.forEach((node) => {
      this.diagram.model.setDataProperty(node.data, 'visible', !setCollapsed);
    });

    return deleteStack;
  }

  private _toggleCollapseTo(node: Part, setCollapsed: boolean): void {
    this.diagram.startTransaction('collapse nodes to');
    this.diagram.model.setDataProperty(node.data, 'isCollapsedTo', setCollapsed);
    this.findNodesToCollapseDown(node, setCollapsed);
    this.diagram.commitTransaction('collapse nodes to');
  }

  public scrollToNode(data: DiagramNodeData | undefined): void {
    if (data && this.diagram) {
      const node = this.diagram.findNodeForData(data);
      if (!node) {
        return;
      }
      this.diagram.scrollToRect(node.actualBounds);
      this.diagram.select(node);
    }
  }

  /**
   * Attention: this function is used by external component, do not delete it
   * Max image size is restricted because on bigger sizes GoJS can fail to export the image
   * That is why we take documentBounds and build a maxImageSizes range array and then
   * try to find an optimal image size to make GoJS generate a raster image using an optimal size and quality.
   * If nothing works it falls back to getImageMaxSizeOptions function as a minimum possible option.
   */
  public exportDiagramImage(): HTMLImageElement {
    const maxImageSizes = getMaxImageSizes(this.diagram.documentBounds);
    for (const maxImageSize of maxImageSizes) {
      const image = this.diagram.makeImage(getImageMaxSizeOptions(this.diagram.documentBounds, maxImageSize));
      if (image && hasImageSrcData(image)) {
        return image;
      }
    }
    return this.diagram.makeImage(
      getImageMaxSizeOptions(this.diagram.documentBounds, DEFAULT_MAX_IMAGE_SIZE),
    ) as HTMLImageElement;
  }

  /**
   * Attention: this function is used by external component, do not delete it
   */
  public exportDiagramSvg(): SVGElement {
    return this.diagram.makeSvg({
      scale: 1,
      maxSize: new Size(this.diagram.documentBounds.width, this.diagram.documentBounds.height),
    });
  }

  public ngAfterViewInit(): void {
    this.setupDiagram();
  }

  // used outside the component
  public zoomToFit(): void {
    this.diagram.commandHandler.zoomToFit();
  }
}
